import { z } from 'zod';

// Zod schemas for data validation

export const ProductionDataSchema = z.object({
  placementDate: z.string().min(1, 'Placement date is required'),
  chickCount: z.number().positive('Chick count must be positive'),
  feedIncoming: z.number().nonnegative('Feed incoming must be non-negative'),
  feedRemaining: z.number().nonnegative('Feed remaining must be non-negative'),
  feedConsumed: z.number().nonnegative('Feed consumed must be non-negative'),
});

export const HarvestEventSchema = z.object({
  type: z.enum(['thinning', 'final']),
  count: z.number().nonnegative('Count must be non-negative'),
  averageWeight: z.number().nonnegative('Average weight must be non-negative'),
  totalWeight: z.number().nonnegative('Total weight must be non-negative'),
  date: z.string().optional(),
});

export const PricingParametersSchema = z.object({
  meatPricePerKg: z.number().positive('Meat price must be positive'),
  feedBonusRate: z.number().nonnegative('Feed bonus rate must be non-negative'),
  feedPenaltyRate: z.number().nonnegative('Feed penalty rate must be non-negative'),
  epefLimits: z.tuple([z.number(), z.number(), z.number()]),
  epefPrices: z.tuple([z.number(), z.number(), z.number()]),
  chickBonusRate: z.number().nonnegative('Chick bonus rate must be non-negative'),
  chickPenaltyRate: z.number().nonnegative('Chick penalty rate must be non-negative'),
  mortalityLimit: z.number().min(0).max(100, 'Mortality limit must be between 0 and 100'),
  coalSupportRate: z.number().nonnegative('Coal support rate must be non-negative'),
});

export const ExpenseEntrySchema = z.object({
  id: z.string(),
  category: z.enum(['coal', 'rice', 'electricity', 'water', 'labor']),
  tonnage: z.number().nonnegative().optional(),
  unitPrice: z.number().nonnegative().optional(),
  totalAmount: z.number().nonnegative('Total amount must be non-negative'),
  vatRate: z.number().min(0).max(100, 'VAT rate must be between 0 and 100'),
  vatExclusiveAmount: z.number().nonnegative('VAT exclusive amount must be non-negative'),
});

// Validation functions

/**
 * Validates that a value is numeric (not NaN or Infinity)
 */
export function validateNumeric(value: unknown): boolean {
  if (typeof value !== 'number') {
    return false;
  }
  return !isNaN(value) && isFinite(value);
}

/**
 * Validates that a number is positive (> 0)
 */
export function validatePositive(value: number): boolean {
  return validateNumeric(value) && value > 0;
}

/**
 * Validates that harvest date is after or equal to placement date
 */
export function validateDateOrder(placementDate: string, harvestDate: string): boolean {
  if (!placementDate || !harvestDate) {
    return false;
  }
  
  const placement = new Date(placementDate);
  const harvest = new Date(harvestDate);
  
  // Check if dates are valid
  if (isNaN(placement.getTime()) || isNaN(harvest.getTime())) {
    return false;
  }
  
  return harvest >= placement;
}

/**
 * Validates that all required fields are present and non-empty
 */
export function validateRequiredFields(data: Record<string, unknown>, requiredFields: string[]): {
  isValid: boolean;
  missingFields: string[];
} {
  const missingFields: string[] = [];
  
  for (const field of requiredFields) {
    const value = data[field];
    
    if (value === undefined || value === null || value === '') {
      missingFields.push(field);
    }
  }
  
  return {
    isValid: missingFields.length === 0,
    missingFields,
  };
}

/**
 * Validates production data using Zod schema
 */
export function validateProductionData(data: unknown) {
  return ProductionDataSchema.safeParse(data);
}

/**
 * Validates harvest event using Zod schema
 */
export function validateHarvestEvent(data: unknown) {
  return HarvestEventSchema.safeParse(data);
}

/**
 * Validates pricing parameters using Zod schema
 */
export function validatePricingParameters(data: unknown) {
  return PricingParametersSchema.safeParse(data);
}

/**
 * Validates expense entry using Zod schema
 */
export function validateExpenseEntry(data: unknown) {
  return ExpenseEntrySchema.safeParse(data);
}
